import { css } from 'lit';

/**
 * CSS custom properties — Amelia-style dark warm theme.
 * Host pages can override via --booking-* custom properties.
 */
export const themeTokens = css`
  :host {
    /* ── Dark warm palette ─────────────────────────────────────────── */
    /* 7 base tokens are host-overridable via --booking-* (Settings → Widget →
       Appearance). Everything else below derives from them with color-mix()
       so a custom theme stays internally consistent without every shade
       needing its own picker — override any derived var directly via its
       own --booking-* for pixel-exact control. */
    --yebo-primary: var(--booking-primary-color, #c4a87a);
    --yebo-primary-hover: var(--booking-primary-hover, color-mix(in srgb, var(--yebo-primary) 88%, black));
    /* Derived from --yebo-primary so fetched/attr themes tint correctly
       (a fixed gold rgba here left themed widgets with gold hovers). */
    --yebo-primary-light: var(--booking-primary-light, color-mix(in srgb, var(--yebo-primary) 12%, transparent));

    /* Dark backgrounds */
    --yebo-bg: var(--booking-bg-color, #1a1a1e);
    --yebo-bg-secondary: var(--booking-bg-secondary, color-mix(in srgb, var(--yebo-bg) 94%, white));
    --yebo-bg-elevated: var(--booking-bg-elevated, color-mix(in srgb, var(--yebo-bg) 88%, white));

    /* Sidebar (warm beige/tan) */
    --yebo-sidebar-bg: var(--booking-sidebar-bg, #c8b898);
    --yebo-sidebar-text: var(--booking-sidebar-text, #2a2420);
    --yebo-sidebar-muted: var(--booking-sidebar-muted, color-mix(in srgb, var(--yebo-sidebar-text) 60%, var(--yebo-sidebar-bg)));
    --yebo-sidebar-active-bg: var(--booking-sidebar-active-bg, color-mix(in srgb, var(--yebo-sidebar-text) 80%, black));
    --yebo-sidebar-active-text: var(--booking-sidebar-active-text, var(--yebo-text));

    /* Text on dark bg */
    --yebo-text: var(--booking-text-color, #e8dcc8);
    --yebo-text-muted: var(--booking-text-muted, #b0a294);
    --yebo-text-heading: var(--booking-text-heading, color-mix(in srgb, var(--yebo-text) 90%, white));

    /* #653 — per-surface colours for the event / ticket screens. Each falls
       back to the token that surface used BEFORE this existed, so an unset
       theme renders byte-identically:
         heading     -> was --yebo-text        (event names, ticket-type labels)
         description -> was --yebo-text-muted  (event blurbs, tier descriptions)
         price       -> was --yebo-primary     (prices on card and ticket step)
       Selector names are deliberately NOT written out here: the widget's style
       tests locate a rule by searching the concatenated styles for its
       selector, so naming one in a comment makes this block match first and
       the real rule invisible. That is exactly what happened when this was
       first written. */
    --yebo-event-heading: var(--booking-event-heading, var(--yebo-text));
    --yebo-event-description: var(--booking-event-description, var(--yebo-text-muted));
    --yebo-event-price: var(--booking-event-price, var(--yebo-primary));

    /* Borders & controls */
    --yebo-border: var(--booking-border-color, #5a5a5e);
    /* Safety colours — not exposed as brand-theme pickers (Settings → Widget
       only offers the 7 base tokens above). */
    --yebo-error: var(--booking-error-color, #e05252);
    --yebo-success: var(--booking-success-color, #5cb85c);

    /* Typography */
    --yebo-font: var(--booking-font-family, system-ui, -apple-system, sans-serif);
    --yebo-font-size-sm: 13px;
    --yebo-font-size-base: 15px;
    --yebo-font-size-lg: 17px;
    --yebo-font-size-xl: 20px;

    /* Spacing */
    --yebo-radius: var(--booking-border-radius, 8px);
    --yebo-radius-sm: calc(var(--yebo-radius) * 0.5);
    --yebo-space-xs: 4px;
    --yebo-space-sm: 8px;
    --yebo-space-md: 16px;
    --yebo-space-lg: 24px;
    --yebo-space-xl: 32px;

    /* Transitions */
    --yebo-transition: 150ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    :host {
      --yebo-transition: 0ms;
    }
  }
`;

/** Reset styles for inside Shadow DOM */
export const resetStyles = css`
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
`;

/** Shared component base styles — dark theme variant */
export const baseStyles = css`
  :host {
    display: block;
    font-family: var(--yebo-font);
    color: var(--yebo-text);
    line-height: 1.5;
  }

  /* ── Mobile: every step's action row pins to the bottom ────────────────
     (design 2026-08-05). All five step components render their Back/Continue
     inside a .actions row; on phones it becomes sticky above the safe area so
     thumbs never chase buttons down a scrolling sheet. Shared here in
     baseStyles because each step carries its own .actions — five copies of
     this rule would drift. */
  @media (max-width: 540px) {
    .actions {
      position: sticky;
      bottom: 0;
      z-index: 2;
      background: var(--yebo-bg);
      border-top: 1px solid var(--yebo-border);
      margin-top: var(--yebo-space-md);
      padding: 12px 0 calc(8px + env(safe-area-inset-bottom, 0px));
    }
    /* Full-width tap targets when only one/two buttons share the row. */
    .actions .btn {
      min-height: 48px;
    }
  }

  *:focus-visible {
    outline: 2px solid var(--yebo-primary);
    outline-offset: 2px;
    border-radius: 2px;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--yebo-space-sm);
    padding: 10px 24px;
    border-radius: var(--yebo-radius);
    font-family: var(--yebo-font);
    font-size: var(--yebo-font-size-base);
    font-weight: 500;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background-color var(--yebo-transition), border-color var(--yebo-transition);
  }

  .btn-primary {
    background: var(--yebo-primary);
    color: var(--yebo-bg);
    border-color: var(--yebo-primary);
  }
  .btn-primary:hover:not(:disabled) {
    background: var(--yebo-primary-hover);
  }
  .btn-primary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: var(--yebo-bg-elevated);
    color: var(--yebo-text);
    border-color: var(--yebo-border);
  }
  .btn-secondary:hover:not(:disabled) {
    background: var(--yebo-bg-secondary);
  }

  .input {
    width: 100%;
    padding: 10px var(--yebo-space-md);
    border: 1px solid var(--yebo-border);
    border-radius: var(--yebo-radius);
    font-family: var(--yebo-font);
    font-size: var(--yebo-font-size-base);
    color: var(--yebo-text);
    background: var(--yebo-bg-elevated);
    transition: border-color var(--yebo-transition);
  }
  .input:focus {
    outline: none;
    border-color: var(--yebo-primary);
    box-shadow: 0 0 0 2px var(--yebo-primary-light);
  }
  .input::placeholder {
    color: var(--yebo-text-muted);
  }
  .input.error {
    border-color: var(--yebo-error);
  }

  .label {
    display: block;
    font-size: var(--yebo-font-size-sm);
    font-weight: 500;
    margin-bottom: var(--yebo-space-xs);
    color: var(--yebo-text-muted);
  }

  .field-error {
    font-size: var(--yebo-font-size-sm);
    color: var(--yebo-error);
    margin-top: var(--yebo-space-xs);
  }

  .step-title {
    font-size: var(--yebo-font-size-xl);
    font-weight: 600;
    color: var(--yebo-text-heading);
    margin-bottom: var(--yebo-space-lg);
  }
`;
